import { USER_TYPE } from "@constants/Enums";
import { error_message } from "@constants/Errors";
import { errorTranslations } from "@constants/ErrorTranslations";
import { NormalizeSpaces } from "@services/Utilities";
import XRegExp from "xregexp";
import { errorMappings } from "@constants/ErrorTranslations";
import { USER_TYPE_STRING } from "@constants/Enums";

XRegExp.install("astral");

export const maxStringLength = 33; // 16 for first name, 16 for last name account for space inbetween

const extractErrorMessage = (error: any) => {
  // errors are sometimes returned as Keycloak error: {errors: an error occurred}
  // which is not correct JSON, so we treat it as a string and extract.

  if (typeof error === "string") {
    const result = getErrorMessage(error);

    if (result) {
      return { errorMessage: result.errorMessage.toLowerCase() };
    }

    try {
      const jsonMatch = error.match(/{.*}/);
      if (jsonMatch) {
        const parsedError = JSON.parse(jsonMatch[0]);
        // if we find error, we need to map it to errorMessage so the rest of the code functions.
        if (parsedError.error) {
          parsedError.errorMessage = parsedError.error;
          delete parsedError.error;
        }
        if (parsedError.errorMessage) {
          return parsedError;
        }
      } else {
        return { errorMessage: error.toLowerCase() };
      }
    } catch (e) {
      console.error(error_message.generic.string_parse, e);
    }
  }

  // if we have an array of errors, we take the first one.
  if (Array.isArray(error)) {
    const errorMessage = error[0];

    const result = getErrorMessage(errorMessage.toLowerCase());

    if (result) {
      return result;
    }

    try {
      const jsonMatch = errorMessage.match(/{.*}/);
      if (jsonMatch) {
        const parsedError = JSON.parse(jsonMatch[0]);
        // if we find error, we need to map it to errorMessage so the rest of the code functions.
        if (parsedError.error) {
          parsedError.errorMessage = parsedError.error;
          delete parsedError.error;
        }
        if (parsedError.errorMessage) {
          return parsedError;
        }
      } else {
        return { errorMessage: error[0].split(",")[0].toLowerCase() };
      }
    } catch (e) {
      console.error(error_message.generic.string_parse, e);
    }
  }

  // handle various types
  if (error?.response && error?.response.data) {
    if (typeof error.response.data === "string") {
      return { errorMessage: error.response.data.toLowerCase() };
    } else if (error.response.data.errorMessage) {
      return error.response.data.toLowerCase();
    } else if (error.response.data.message) {
      return { errorMessage: error.response.data.message.toLowerCase() };
    } else if (error.error_description) {
      return { errorMessage: error.error_description.toLowerCase() };
    }
  }

  if (error?.data?.error) {
    const errorPrint = error.data.error;
    return { errorMessage: errorPrint.split(",")[0].toLowerCase() };
  }
  // return the extracted message, or a generic error
  return {
    errorMessage:
      error?.response?.data?.message.toLowerCase() ||
      error?.message.toLowerCase() ||
      error_message.generic.common.toLowerCase(),
  };
};

function getErrorMessage(error: string): { errorMessage: string } | null {
  for (const mapping of errorMappings) {
    if (error.toLocaleLowerCase().includes(mapping.keyword)) {
      return { errorMessage: mapping.message };
    }
  }
  return null;
}

/**
 * TranslateErrorMessage - translates an error message(s) from English to Japanese
 *
 * Using an extracted message, we find it in the map and translate it to Japanese.
 *
 * @param {Object} error - The error message.
 *
 **/
export const TranslateErrorMessage = (error: any) => {
  console.error("Translating error message:", error);
  const errorDetails = extractErrorMessage(error);
  console.error("Extracted error details:", errorDetails);

  const { errorMessage, ...otherDetails } = errorDetails;
  const translation = errorTranslations[errorMessage];

  if (typeof translation === "function") {
    return translation(otherDetails);
  }

  if (errorDetails?.error_description) {
    return errorTranslations[errorDetails.error_description];
  }

  const roleErrorRegex = /Couldn't find Role with 'id'=\d+/;

  if (roleErrorRegex.test(error)) {
    return error_message.role.not_found;
  }

  return translation || error_message.generic.common;
};

// Handles regex matching of kana input fields
export const handleKanaChange = (
  key: string,
  value: string,
  updateFunc: (key: string, value: string) => void,
  setError: (key: string, value: boolean) => void,
) => {
  const regex = /^[\u3040-\u309F\u30A0-\u30FF\s]*$/;

  const normalizedText = NormalizeSpaces(value);

  const lengthError = `${normalizedText}_length`;

  if (normalizedText.length > maxStringLength) {
    setError(lengthError, true);
  } else {
    setError(lengthError, false);
  }

  if (regex.test(normalizedText) || normalizedText === "") {
    setError(key, false);
  } else {
    setError(key, true);
  }

  updateFunc(key, normalizedText);
};

// Handles regex matching of kana input fields only accepting hiragana
export const handleHiraganaKanaChange = (
  key: string,
  value: string,
  updateFunc: (key: string, value: string) => void,
  setError: (key: string, value: boolean) => void,
) => {
  const regex = XRegExp("^(?:\\p{Hiragana}|\\p{Katakana}|\\s|ー){1,32}$");

  const normalizedText = NormalizeSpaces(value);

  const lengthError = `${normalizedText}_length`;

  if (normalizedText.length > maxStringLength) {
    setError(lengthError, true);
  } else {
    setError(lengthError, false);
  }

  if (regex.test(normalizedText) || normalizedText === "") {
    setError(key, false);
  } else {
    setError(key, true);
  }

  updateFunc(key, normalizedText);
};

// Handles regex matching of roman (english) input fields
export const handleRomanChange = (
  key: string,
  value: string,
  updateFunc: (key: string, value: string) => void,
  setError: (key: string, value: boolean) => void,
) => {
  const regex = /^[a-zA-Z\-\s]*$/;

  const normalizedText = NormalizeSpaces(value).trim();

  const lengthError = `${normalizedText}_length`;

  if (normalizedText.length > maxStringLength) {
    setError(lengthError, true);
  } else {
    setError(lengthError, false);
  }

  if (regex.test(normalizedText) || normalizedText === "") {
    setError(key, false);
  } else {
    setError(key, true);
  }

  updateFunc(key, normalizedText);
};

export const handleNumericalCheck = (
  key: string,
  value: string,
  updateFunc: (key: string, value: string) => void,
  setError: (key: string, value: boolean) => void,
) => {
  const regex = /[0-9]/;

  const normalizedText = NormalizeSpaces(value);

  if (regex.test(normalizedText)) {
    setError(key, true);
  } else {
    setError(key, false);
  }

  updateFunc(key, normalizedText);
};

export const handleNameCheck = (
  key: string,
  value: string,
  updateFunc: (key: string, value: string) => void,
  setError: (key: string, value: boolean) => void,
) => {
  const nameRegex = XRegExp(
    "^(?:\\p{Han}|\\p{Hiragana}|\\p{Katakana}|[\\uFF65-\\uFF9F]|[A-Za-z0-9]|[\\uFF10-\\uFF19\\uFF21-\\uFF3A\\uFF41-\\uFF5A]|[._@\\-]|々|ー|[！＠＃＄％＾＆＊（）－＿＝＋［］｛｝；：＇＂，．／＜＞？＼｜～【】・　]){1,16}$",
  );

  let normalizedText = NormalizeSpaces(value);

  if (normalizedText.length > 16) {
    normalizedText = normalizedText.slice(0, 16);
  }

  if (normalizedText == "" || normalizedText == null) {
    updateFunc(key, normalizedText);
    setError(key, false);
    return;
  }

  if (!nameRegex.test(normalizedText)) {
    setError(key, true);
  } else {
    setError(key, false);
  }

  updateFunc(key, normalizedText);
};

export const handleNoValidationNameCheck = (
  key: string,
  value: string,
  updateFunc: (key: string, value: string) => void,
  setError: (key: string, value: boolean) => void,
) => {
  const max_name_length = 16;
  let normalizedText = NormalizeSpaces(value);

  if (normalizedText.length > max_name_length) {
    normalizedText = normalizedText.slice(0, max_name_length);
  }

  if (normalizedText == "" || normalizedText == null) {
    updateFunc(key, normalizedText);
    setError(key, false);
    return;
  }

  updateFunc(key, normalizedText);
};

// Handles regex matching of numeric input fields
export const handleNumericalFieldChange = (
  key: string,
  value: string,
  updateFunc: (key: string, value: string) => void,
  setError: (key: string, value: boolean) => void,
) => {
  const regex = /^[0-9]*$/;

  const normalizedText = NormalizeSpaces(value).trim();

  if (regex.test(normalizedText) || normalizedText === "") {
    setError(key, false);
  } else {
    setError(key, true);
  }

  updateFunc(key, normalizedText);
};

export const handleEmployeeNumberChange = (
  value: string,
  updateFunc: (key: string, value: string) => void,
  setError: (key: string, value: boolean) => void,
) => {
  const regex = /^[A-Za-z0-9]*$/;
  const key = "employee_number";

  const normalizedText = NormalizeSpaces(value).trim();

  if (regex.test(normalizedText) || normalizedText === "") {
    setError(key, false);
  } else {
    setError(key, true);
  }
  updateFunc(key, normalizedText);
};

// Handles regex matching of postcode input fields
export const handlePostCodeChange = (
  key: string,
  value: string,
  updateFunc: (key: string, value: string) => void,
  setError: (key: string, value: boolean) => void,
) => {
  const regex = /^[0-9]{7}$/;
  let normalizedText = NormalizeSpaces(value).trim();

  if (normalizedText.length > 7) {
    normalizedText = normalizedText.slice(0, 7);
  }

  if (regex.test(normalizedText) || normalizedText === "") {
    setError(key, false);
  } else {
    setError(key, true);
  }
  updateFunc(key, normalizedText);
};

// Handles regex matching of phone number input fields
export const phoneNumberChange = (
  key: string,
  value: string,
  updateFunc: (key: string, value: string) => void,
  setError: (key: string, value: boolean) => void,
  userType = USER_TYPE.EXTERNAL,
) => {
  const isExternalUser =
    userType === USER_TYPE.EXTERNAL || userType === USER_TYPE_STRING.EXTERNAL;
  // const isInternalUser =
  //   userType === USER_TYPE.INTERNAL || userType === USER_TYPE_STRING.INTERNAL;
  const isTelRestrictedUser = isExternalUser;

  const regex = isTelRestrictedUser ? /^[0-9]{10,11}$/ : /^[0-9]+$/;
  let normalizedText = NormalizeSpaces(value).trim();

  if (normalizedText.length > 11 && isTelRestrictedUser) {
    normalizedText = normalizedText.slice(0, 11);
  }

  if (regex.test(normalizedText) || normalizedText === "") {
    setError(key, false);
  } else {
    setError(key, true);
  }
  updateFunc(key, normalizedText);
};

// Handles regex matching of tenant sub directory input fields for tenant creation
export const handleLoginChange = (
  value: string,
  updateFunc: (key: string, value: string) => void,
  setError: (key: string, value: boolean) => void,
) => {
  const regex = /^[a-zA-Z0-9._@-]{3,64}$/;

  const normalizedText = NormalizeSpaces(value).trim().slice(0, 64);

  if (normalizedText.length < 3) {
    if (normalizedText !== "") {
      setError("login_id_length", true);
    }
  } else {
    setError("login_id_length", false);
    if (regex.test(normalizedText) || normalizedText === "") {
      setError("login_id", false);
    } else {
      setError("login_id", true);
    }
  }
  updateFunc("login_id", normalizedText);
};

// Handles regex matching of tenant sub directory input fields for tenant creation
export const handleSubDirectoryChange = (
  key: string,
  value: string,
  updateFunc: (key: string, value: string) => void,
  setError: (key: string, value: boolean) => void,
) => {
  const regex = /^[A-Za-z0-9_]{6}$/;

  const normalizedText = NormalizeSpaces(value).trim().slice(0, 6);

  if (regex.test(normalizedText) || normalizedText === "") {
    setError(`${key}`, false);
  } else {
    setError(`${key}`, true);
  }

  updateFunc(key, normalizedText);
};

// Handles regex matching of tenant code input fields for tenant creation
export const handleCodeChange = (
  key: string,
  value: string,
  updateFunc: (key: string, value: string) => void,
  setError: (key: string, value: boolean) => void,
) => {
  const regex = /^[A-Za-z0-9_]{5,64}$/;

  let normalizedText = NormalizeSpaces(value).trim();

  if (normalizedText.length > 64) {
    normalizedText = normalizedText.slice(0, 64);
  }

  if (regex.test(normalizedText) || normalizedText === "") {
    setError(`${key}`, false);
  } else {
    setError(`${key}`, true);
  }

  updateFunc(key, normalizedText);
};

// Handles regex matching of tenant department code input fields for tenant creation
export const handleTenantDepartmentCodeChange = (
  key: string,
  value: string,
  updateFunc: (key: string, value: string) => void,
  setError: (key: string, value: boolean) => void,
) => {
  const regex = /^[A-Za-z0-9]+$/;

  let normalizedText = NormalizeSpaces(value).trim();

  if (normalizedText.length > 64) {
    normalizedText = normalizedText.slice(0, 64);
  }

  if (regex.test(normalizedText) || normalizedText === "") {
    setError(`${key}`, false);
  } else {
    setError(`${key}`, true);
  }

  updateFunc(key, normalizedText);
};

// Handles regex matching of passwords to a specified policy number input fields
export const handlePasswordChange = (
  key: string,
  value: string,
  updateFunc: (value: string) => void,
  setError: (key: string, value: boolean) => void,
) => {
  const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

  const normalizedText = NormalizeSpaces(value).trim();

  if (regex.test(normalizedText) || normalizedText === "") {
    setError(key, false);
  } else {
    setError(key, true);
  }

  updateFunc(normalizedText);
};

// Handles regex matching of passwords to a specified policy number input fields
export const handleUserPasswordChange = (
  key: string,
  value: string,
  updateFunc: (key: string, value: string) => void,
  setError: (key: string, value: boolean) => void,
) => {
  const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

  const normalizedText = NormalizeSpaces(value).trim();

  if (regex.test(normalizedText) || normalizedText === "") {
    setError(key, false);
  } else {
    setError(key, true);
  }

  updateFunc(key, normalizedText);
};
